import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  useSocialListCommentsQuery,
  socialListCommentsQueryRootKey,
  socialListCommentsQueryKey,
} from '@web/gen/feed'
import type { SocialListCommentsQuery } from '@web/gen/feed'
import type { SocialComment } from 'codegen/types'

export function useComments(postId: string, parentCommentId?: string, hasChildren?: boolean) {
  const queryClient = useQueryClient()
  const [comments, setComments] = React.useState<SocialComment[]>([])
  const [cursorPage, setCursorPage] = React.useState<string | null>(null)
  const [isFetchingNextPage, setIsFetchingNextPage] = React.useState(false)
  const [hasNextPage, setHasNextPage] = React.useState(false)
  const localCommentsRef = React.useRef<SocialComment[]>([])
  const isOptimisticPost = postId.startsWith('optimistic-')
  const shouldFetch = !isOptimisticPost && (!parentCommentId || hasChildren)
  const variables = {
    input: {
      postID: postId,
      parentCommentID: parentCommentId,
      itemsPerPage: 10,
      cursor: cursorPage ? cursorPage : undefined,
    },
  }

  const {
    data,
    isFetching,
    isLoading: isPending,
    refetch,
  } = useSocialListCommentsQuery(
    {
      input: variables.input,
    },
    { enabled: shouldFetch },
  )

  React.useEffect(() => {
    if (!shouldFetch || !data) {
      return
    }
    const commentsArray = data.socialListComments?.comments || []
    const serverIds = new Set(commentsArray.map(c => c.id))
    const pendingLocal = localCommentsRef.current.filter(c => !serverIds.has(c.id))

    setComments(prevComments => {
      if (!cursorPage) {
        return [...pendingLocal, ...commentsArray]
      }

      const combinedComments = [...pendingLocal, ...prevComments, ...commentsArray]
      const uniqueCommentsMap = new Map()
      combinedComments.forEach(comment => uniqueCommentsMap.set(comment.id, comment))

      return [...uniqueCommentsMap.values()]
    })

    localCommentsRef.current = pendingLocal

    setHasNextPage(data.socialListComments?.pagination?.hasNextPage ?? false)
    setIsFetchingNextPage(false)
  }, [data, shouldFetch, cursorPage])

  const handleLoadMore = () => {
    if (data?.socialListComments?.pagination?.nextPageCursor) {
      setIsFetchingNextPage(true)
      setCursorPage(data?.socialListComments?.pagination?.nextPageCursor)
    }
  }

  const handleNewComment = (newComment: SocialComment) => {
    localCommentsRef.current = [newComment, ...localCommentsRef.current]
    setComments(prevComments => [newComment, ...prevComments])

    const typedComment = newComment as NonNullable<
      SocialListCommentsQuery['socialListComments']['comments']
    >[number]

    // Check if a query with actual data exists for this parent comment
    const existingQueries = queryClient.getQueriesData<SocialListCommentsQuery>({
      queryKey: [socialListCommentsQueryRootKey],
      predicate: query => {
        const vars = query.queryKey.find(
          k => typeof k === 'object' && k !== null && 'input' in k,
        ) as { input?: { postID?: string; parentCommentID?: string } } | undefined
        return vars?.input?.postID === postId && vars?.input?.parentCommentID === parentCommentId
      },
    })

    // Only consider queries that actually have data (disabled queries exist in the cache but with no data)
    const queriesWithData = existingQueries.filter(([, data]) => data?.socialListComments?.comments)

    if (queriesWithData.length > 0) {
      // Update existing queries that have data
      queryClient.setQueriesData<SocialListCommentsQuery>(
        {
          queryKey: [socialListCommentsQueryRootKey],
          predicate: query => {
            const vars = query.queryKey.find(
              k => typeof k === 'object' && k !== null && 'input' in k,
            ) as { input?: { postID?: string; parentCommentID?: string } } | undefined
            return (
              vars?.input?.postID === postId && vars?.input?.parentCommentID === parentCommentId
            )
          },
        },
        oldData => {
          if (!oldData?.socialListComments?.comments) {
            return oldData
          }
          if (oldData.socialListComments.comments.some(c => c.id === newComment.id)) {
            return oldData
          }
          return {
            ...oldData,
            socialListComments: {
              ...oldData.socialListComments,
              comments: [typedComment, ...oldData.socialListComments.comments],
            },
          }
        },
      )
    } else {
      // Seed the cache so the query won't fetch when it becomes enabled
      const seedQueryKey = socialListCommentsQueryKey({
        input: {
          postID: postId,
          parentCommentID: parentCommentId,
          itemsPerPage: 10,
        },
      })
      queryClient.setQueryData<SocialListCommentsQuery>(seedQueryKey, {
        socialListComments: {
          __typename: 'SocialCommentsListResponse',
          comments: [typedComment],
          pagination: null,
          errors: null,
        },
      })
    }
  }

  const bumpCommentReplyCount = React.useCallback(
    (commentId: string) => {
      queryClient.setQueriesData<SocialListCommentsQuery>(
        {
          queryKey: [socialListCommentsQueryRootKey],
          predicate: query => {
            const vars = query.queryKey.find(
              k => typeof k === 'object' && k !== null && 'input' in k,
            ) as { input?: { postID?: string } } | undefined
            return vars?.input?.postID === postId
          },
        },
        oldData => {
          if (!oldData?.socialListComments?.comments) {
            return oldData
          }
          const comments = oldData.socialListComments.comments
          const idx = comments.findIndex(c => c.id === commentId)
          if (idx === -1) {
            return oldData
          }
          const updated = [...comments]
          updated[idx] = {
            ...updated[idx],
            commentsCount: (updated[idx].commentsCount || 0) + 1,
          }
          return {
            ...oldData,
            socialListComments: {
              ...oldData.socialListComments,
              comments: updated,
            },
          }
        },
      )
    },
    [queryClient, postId],
  )

  const decrementCommentReplyCount = React.useCallback(
    (commentId: string) => {
      queryClient.setQueriesData<SocialListCommentsQuery>(
        {
          queryKey: [socialListCommentsQueryRootKey],
          predicate: query => {
            const vars = query.queryKey.find(
              k => typeof k === 'object' && k !== null && 'input' in k,
            ) as { input?: { postID?: string } } | undefined
            return vars?.input?.postID === postId
          },
        },
        oldData => {
          if (!oldData?.socialListComments?.comments) {
            return oldData
          }
          const comments = oldData.socialListComments.comments
          const idx = comments.findIndex(c => c.id === commentId)
          if (idx === -1) {
            return oldData
          }
          const updated = [...comments]
          updated[idx] = {
            ...updated[idx],
            commentsCount: Math.max(0, (updated[idx].commentsCount || 0) - 1),
          }
          return {
            ...oldData,
            socialListComments: {
              ...oldData.socialListComments,
              comments: updated,
            },
          }
        },
      )
    },
    [queryClient, postId],
  )

  return {
    comments,
    refetch,
    isFetching,
    isFetchingNextPage,
    isLoading: isPending,
    handleLoadMore,
    handleNewComment,
    bumpCommentReplyCount,
    decrementCommentReplyCount,
    hasNextPage,
  }
}
